import { useCallback, useRef, useState } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import {
  resetWaterfallAgreement,
  uploadWaterfallAgreement,
} from '@/services/waterfall'
import type { WaterfallAgreementMeta } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import WaterfallAgreementReader from './WaterfallAgreementReader'
import WaterfallTabHeader from './WaterfallTabHeader'

function agreementSubtitle(meta: WaterfallAgreementMeta | null): string {
  if (!meta) return 'Loading agreement…'
  if (meta.is_custom) {
    const uploaded = meta.uploaded_at
      ? new Date(meta.uploaded_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null
    return uploaded
      ? `${meta.original_filename} · uploaded ${uploaded}`
      : meta.original_filename
  }
  return 'Default bundled document'
}

export default function Conventions() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [agreementMeta, setAgreementMeta] = useState<WaterfallAgreementMeta | null>(null)
  const [readerKey, setReaderKey] = useState(0)
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleMetaLoaded = useCallback((meta: WaterfallAgreementMeta) => {
    setAgreementMeta(meta)
  }, [])

  const handleUpload = async (file: File) => {
    setSaving(true)
    setActionError(null)
    try {
      const updated = await uploadWaterfallAgreement(file)
      setAgreementMeta(updated)
      setReaderKey((key) => key + 1)
    } catch (e) {
      setActionError(formatApiError(e, 'Failed to upload agreement'))
    } finally {
      setSaving(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleReset = async () => {
    setSaving(true)
    setActionError(null)
    try {
      const updated = await resetWaterfallAgreement()
      setAgreementMeta(updated)
      setReaderKey((key) => key + 1)
    } catch (e) {
      setActionError(formatApiError(e, 'Failed to restore default agreement'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="w-full">
      <WaterfallTabHeader
        title="Conventions"
        description="Definitions and waterfall priorities from the operating agreement, and how BookKeeper applies them."
      />

      <div className="grid gap-8 xl:grid-cols-2">
        <div className="min-w-0 space-y-8 text-sm text-gray-700">
          <section>
            <h3 className="font-semibold text-gray-900">Overview</h3>
            <p className="mt-2">
              When the Board distributes available cash outside a Liquidity Event, proceeds flow
              through four Operations Waterfall tiers. Net proceeds from a Liquidity Event follow a
              separate waterfall. Unreturned HP Threshold governs Operations Tiers 2 and 3 and
              Liquidity Event Tier 1: HP Threshold as of the date, minus Class A distributions under
              those sections, plus any such distributions the Class A Members return to the Company.
              BookKeeper tracks the daily HP balance on the <strong>Balances</strong> tab and uses
              it to size and cap each operations tier on the Calculator.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">Defined terms</h3>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>
                <strong>HP Threshold</strong> — $3,707,950.47 plus a return at the Prime Rate,
                compounded quarterly, commencing on April 1, 2025.
              </li>
              <li>
                <strong>HP Threshold Proportions</strong> — 10.7876% BR/KR (KB) and 89.2124% JR.
              </li>
              <li>
                <strong>Unreturned HP Threshold</strong> — HP Threshold as of the date of
                determination, minus Class A distributions under Operations Tiers 2 and 3 and
                Liquidity Event Tier 1, plus any of those distributions returned to the Company.
              </li>
              <li>
                <strong>RO Threshold</strong> — $6,491,000. Unreturned RO Threshold is that amount
                minus JR&apos;s Liquidity Event Tier 2 receipts, plus any of those distributions JR
                returns.
              </li>
              <li>
                <strong>Liquidity Event</strong> — (a) a new or expanded third-party term loan
                whose net proceeds are distributed to Members, or (b) a Change in Control (sale,
                transfer, merger, reorganization, consolidation, or similar).
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">Operations waterfall</h3>
            <ol className="mt-2 list-decimal space-y-2 pl-5">
              <li>
                <strong>Tier 1</strong> — 50% to LVW and 50% to TR until each has received
                cumulative distributions equal to (days from October 13, 2025 through the
                distribution date) × $150,000 ÷ 365. BookKeeper day-counts inclusively (days + 1)
                and true-ups against logged history.
              </li>
              <li>
                <strong>Tier 2</strong> — 100% to Class A in HP Threshold Proportions until
                cumulative Class A receipts equal 200% of cumulative Tier 1. The engine also stops
                Tier 2 when Unreturned HP Threshold reaches $0.
              </li>
              <li>
                <strong>Tier 3</strong> — 66.6667% to Class A (HP proportions) and 33.3333% to
                Class B (by Class B units) until Unreturned HP Threshold is $0. BookKeeper uses
                vesting-aware Class B allocation and withholds Scott&apos;s vested share.
              </li>
              <li>
                <strong>Tier 4 (Thereafter)</strong> — Class A and Class B pro rata by Class A +
                Class B units, with the same vesting-aware Class B allocation and Scott withholding.
              </li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">Liquidity Event waterfall</h3>
            <ol className="mt-2 list-decimal space-y-2 pl-5">
              <li>
                100% to Class A in HP Threshold Proportions until Unreturned HP Threshold is $0.
              </li>
              <li>100% to JR until Unreturned RO Threshold is $0.</li>
              <li>
                Thereafter, Class A and Class C pro rata by Class A + Class C units.
              </li>
            </ol>
            <p className="mt-2">
              BookKeeper&apos;s Calculator models the Operations Waterfall only. Income allocation
              uses this Liquidity Event ordering for capital-account targets (see Methodology); cash
              liquidity-event distributions are not logged here.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">Members</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li><strong>JR</strong> — Julie &amp; Edward Rhyne (Class A)</li>
              <li><strong>KB / BR/KR</strong> — Brittany &amp; Keller Rhyne (Class A)</li>
              <li><strong>LVW</strong> — Lauren &amp; David Van Wagenen (Class B and Class C)</li>
              <li><strong>TR</strong> — Alicia &amp; Taylor Rhyne (Class B and Class C)</li>
              <li><strong>SM</strong> — Scott McClelland (Class B and Class C)</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">How BookKeeper models the HP Threshold</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <strong>Opening conversion balance</strong> — $2,491,919.48 on 2025-04-01.
              </li>
              <li>
                <strong>Increases</strong> — Class A capital contributions after the conversion date.
              </li>
              <li>
                <strong>Preference</strong> — accrues daily at Prime on the post-flow balance,
                Actual/360.
              </li>
              <li>
                <strong>Compounding</strong> — last business day of each calendar quarter (federal
                holidays from Settings).
              </li>
              <li>
                <strong>Repayments</strong> — Class A distributions in Tier 2 and Tier 3A reduce
                the balance on the distribution log / JE date (ACH initiation), not the bank-clear
                date. Dist #11 is the exception: the 3/3 repayment, 3/5 ACH-return issuance, and
                3/10 re-send are all modeled. Tier 2 and Tier 3 stop once Unreturned HP Threshold
                reaches $0.
              </li>
            </ul>
            <p className="mt-2">
              The $3,707,950.47 agreement figure is the 12/31/2025 capitalized balance (conversion
              + post-conversion issuances + 2025 capitalized preference) and is the denominator for
              the 10.7876% / 89.2124% proportions.
            </p>
            <p className="mt-2">
              <strong>Economic balance</strong> on the Balances tab = capitalized balance plus
              preference accrued since the last compounding date.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">Vesting catch-up</h3>
            <p className="mt-2 text-sm text-gray-700 space-y-2">
              <span className="block">
                Scott&apos;s vested Class B share in each ordinary distribution is withheld from
                cash, declared to his Tier 3 Distributions account, and accrued to Distributions
                Payable until paid via catch-up.
              </span>
              <span className="block">
                His unvested share is paid to LVW and TR in cash now; LVW and TR are never docked for
                Scott&apos;s vesting.
              </span>
              <span className="block">
                Catch-up payments draw first from that payable balance, then from new cash when
                milestone step-ups increase what is payable.
              </span>
              <span className="block">
                The Accruals tab measures Tier 3B and Tier 4B on the vesting basis as of each
                distribution date and excludes catch-up payments.
              </span>
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">Floor-at-zero rule</h3>
            <p className="mt-2">
              Each tier pays at most the cash remaining going into that tier; no member receives a
              negative amount. Retroactive corrections are deferred until a future distribution has
              cash in that tier.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900">Rounding</h3>
            <p className="mt-2">
              Half-away-from-zero to cents (
              <code className="rounded bg-gray-100 px-1 text-xs">R2</code>
              ), matching Google Sheets ROUND.
            </p>
          </section>
        </div>

        <aside className="min-w-0 w-full xl:sticky xl:top-4 xl:self-start">
          <div className="w-full overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="container-title">Distribution waterfall agreement</h3>
                  <p className="mt-0.5 text-xs text-gray-500">{agreementSubtitle(agreementMeta)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {agreementMeta?.is_custom && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void handleReset()}
                      className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
                    >
                      Restore default
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                    {saving ? 'Saving…' : 'Replace…'}
                  </button>
                </div>
              </div>
              {actionError && (
                <p className="mt-2 text-xs text-red-600" role="alert">
                  {actionError}
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                disabled={saving}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void handleUpload(file)
                }}
              />
            </div>
            <WaterfallAgreementReader
              key={readerKey}
              refreshKey={readerKey}
              onMetaLoaded={handleMetaLoaded}
              className="w-full max-h-[calc(100vh-10rem)] overflow-y-auto px-4 py-3"
            />
          </div>
        </aside>
      </div>
    </div>
  )
}
